<template>
    <div class="p-2 md:p-6 mt-4">
        <div class="flex items-center justify-between flex-wrap gap-2">
            <h2 class="font-bold uppercase tracking-wider">
                {{ $t("apartments.budget") }}
            </h2>
            <div class="flex items-center gap-4 flex-wrap">
                <!-- Thema 1 (Spec
                     docs/superpowers/specs/2026-08-04-welle2-spalten-sichtbarkeit-design.md):
                     Wohnungs-Einstieg der Spalten-Sichtbarkeit — nur Advisor
                     (Entscheid Luca 3), bearbeitet die Wohnung, nicht das Projekt. -->
                <button
                    v-if="isAdvisor && columnsSettingsLink"
                    type="button"
                    class="flex items-center gap-1 text-sm text-minplan-darkgreybeige hover:underline"
                    @click="$inertia.visitInModal(columnsSettingsLink)"
                >
                    <TableIcon class="h-4 w-4" />
                    {{ $t("columns.manage") }}
                </button>
                <!-- Welle 3, Thema 5 (Spec
                     docs/superpowers/specs/2026-08-04-welle3-thema5-excel-abrechnung-design.md):
                     XLSX-Export der Abrechnungstabelle — echtes <a href> (kein
                     Inertia-XHR), sichtbar sobald die Prop gesetzt ist (Advisor UND
                     Käufer — die Page reicht die jeweils richtige Route durch). -->
                <!-- Auftrag Peter, 2026-09-01: der Export nimmt die Stellung
                     des Schalters rechts daneben mit. Die Datei zeigt damit,
                     was der Betreuer beim Klicken vor sich hatte — bis
                     hierhin war sie immer brutto, egal wie die Tabelle
                     darunter stand. -->
                <a
                    v-if="budgetExportLink"
                    :href="budgetExportHref"
                    class="flex items-center gap-1 text-sm text-minplan-darkgreybeige hover:underline"
                >
                    <DownloadIcon class="h-4 w-4" />
                    {{ $t("exports.budget") }}
                </a>
                <!-- Welle 3, Thema 12a (Spec
                     docs/superpowers/specs/2026-08-04-welle3-thema12a-kaeufer-archiv-design.md):
                     Archiv-Download der Wohnung — nur beim Käufer (BACKLOG-Vorgabe;
                     der Betreuer hat sein Archiv auf Projektebene), echtes <a href>. -->
                <a
                    v-if="!isAdvisor && archiveExportLink"
                    :href="archiveExportLink"
                    class="flex items-center gap-1 text-sm text-minplan-darkgreybeige hover:underline"
                >
                    <DownloadIcon class="h-4 w-4" />
                    {{ $t("exports.archive") }}
                </a>
                <!-- ANFORDERUNGEN-V2 V2-01: Umschalter zwischen zwei
                     gleichwertigen Ansichten (inkl./exkl. MWST) —
                     Schiebeschalter statt Kaestchen. Die Schreibweise "MWST"
                     ist so gewollt und bleibt unveraendert.

                     Bewusst der BESTEHENDE `SwitchInput` (Headless-UI-`Switch`,
                     bringt `role="switch"`, `aria-checked` und die
                     Tastaturbedienung mit) statt einer zweiten eigenen
                     Schalter-Komponente — er ist bereits an vier Stellen im
                     Einsatz. Seine Ein-Farbe wurde im selben Zug auf die
                     Markenfarbe gezogen, was auch die vier Bestandsschalter
                     der Grundregel unterwirft. -->
                <!-- `compact`: der Umschalter steht hier neben Textaktionen
                     («Spalten verwalten», «Export») und unter der Überschrift
                     «Abrechnung» — in der Normalgrösse wog er optisch
                     schwerer als beide, obwohl er die geringste Tragweite
                     hat. -->
                <switch-input
                    compact
                    v-model="inclVat"
                    :label="inclVat ? $t('apartments.inclVat') : $t('apartments.exclVat')"
                ></switch-input>
            </div>
        </div>

        <apartment-budget-table
            :apartment="apartment"
            :topics="topicCosts.topics"
            :total="topicCosts.total"
            :mwst="inclVat"
            :visible-columns="tableVisibleColumns"
            :is-budget-based="isBudgetBased === true"
        ></apartment-budget-table>
    </div>
</template>

<script>
import { DownloadIcon, TableIcon } from "@heroicons/vue/outline";
import ApartmentBudgetTable from "@/Shared/Components/Apartments/ApartmentBudgetTable";
import SwitchInput from "@/Shared/Components/SwitchInput";

// Themen 2-4 (Spec docs/superpowers/specs/2026-08-04-welle2-tabelle-detailebene-design.md,
// Klärungen aus dem B-Review): Die alte Fortschritts-/Prozentanzeige
// ("{{ total.subtotal }} / {{ total.budget }}" + Progress-Bar) entfällt —
// `subtotal` hat seit Auftrag B eine neue Bedeutung (inkl. Gebühren) und war
// als Fortschrittsmass nicht mehr sinnvoll. An ihrer Stelle steht der
// MwSt-Umschalter (Default inkl.), der die `mwst`-Prop an
// ApartmentBudgetTable durchreicht.
//
// Thema 1 (Spec docs/superpowers/specs/2026-08-04-welle2-spalten-sichtbarkeit-design.md):
// `isAdvisor`/`visibility`/`columnsSettingsLink`/`visibleColumns` sind neu.
// Für den Advisor filtert die Tabelle NICHT sicherheitshalber (das Backend
// nullt nur beim Buyer), sondern rein als Anzeige-Präferenz über
// `visibility.internal`. Der Buyer bekommt seinen bereits serverseitig
// angewandten (und ggf. genullten) `visibleColumns`-Satz durchgereicht;
// fehlt die Prop (Default-Fall), zeigt die Tabelle alle Spalten (siehe
// `show()` in ApartmentBudgetTable.vue).
export default {
    props: [
        "apartment",
        "topics",
        "topicCosts",
        "isAdvisor",
        "visibility",
        "visibleColumns",
        // Auftrag Luca, 2026-08-17: durchgereicht an die Tabelle, die daraus
        // die Mehrkosten-Spalte schaltet.
        "isBudgetBased",
        "columnsSettingsLink",
        "budgetExportLink",
        "archiveExportLink",
    ],
    components: {
        ApartmentBudgetTable,
        SwitchInput,
        TableIcon,
        DownloadIcon,
    },
    data() {
        return {
            // Auftrag Peter, 2026-09-01: DIE VORGABE HAENGT AN DER
            // ZIELGRUPPE. Der Kaeufer schuldet den Betrag inklusive MWST und
            // sieht ihn so; die Betreuung arbeitet gegen Unternehmer, die
            // exklusive rechnen. Beide koennen weiterhin umschalten — nur der
            // Startpunkt ist ein anderer.
            inclVat: !this.isAdvisor,
        };
    },
    computed: {
        /**
         * Die Stellung des Schalters als Query-String am Download-Link.
         *
         * Nur beim Betreuer: die Kaeufer-Route ist fest inklusive
         * (`Client\Exports\ApartmentExportController`) und wuerde den
         * Parameter ohnehin nicht lesen — ihn trotzdem anzuhaengen, hiesse
         * eine Wahl anzubieten, die es dort nicht gibt.
         */
        budgetExportHref() {
            if (!this.isAdvisor || !this.budgetExportLink) {
                return this.budgetExportLink;
            }

            const trenner = this.budgetExportLink.includes("?") ? "&" : "?";

            return `${this.budgetExportLink}${trenner}vat=${
                this.inclVat ? "incl" : "excl"
            }`;
        },
        tableVisibleColumns() {
            if (this.isAdvisor) {
                return this.visibility ? this.visibility.internal : null;
            }
            return this.visibleColumns ?? null;
        },
    },
};
</script>

<style scoped></style>
